import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Target, Eye } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { supabase } from "@/integrations/supabase/client";

const Logo3D = lazy(() => import("@/components/Logo3D"));

type Leader = {
  id: string;
  name: string;
  role: string;
  slug: string;
  image_url: string | null;
  image_position: string;
  tagline: string | null;
};

type DbTeamMember = {
  id: string;
  name: string;
  role: string;
  image_url: string | null;
  image_position: string;
};

type DbClient = {
  id: string;
  name: string;
  subtitle: string | null;
  image_url: string | null;
};

const stats = [
  { value: "350+", label: "Projects Completed" },
  { value: "10+", label: "Years of Experience" },
  { value: "15+", label: "Awards Won" },
  { value: "300+", label: "Happy Clients" },
];

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About Dhivisha Architects",
  "description": "Meet the COA-registered team behind Dhivisha Architects. 350+ projects, 15+ awards, 10+ years of innovative architectural design in Kumbakonam.",
  "mainEntity": { "@id": "https://dhivishaarchitects.com/#organization" },
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["h1", ".about-intro"],
  },
};

const About = () => {
  const { data: leaders = [] } = useQuery({
    queryKey: ["leaders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leaders")
        .select("id, name, role, slug, image_url, image_position, tagline")
        .order("sort_order");
      if (error) throw error;
      return data as Leader[];
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name, subtitle, image_url")
        .order("sort_order");
      if (error) throw error;
      return data as DbClient[];
    },
  });

  const { data: team = [] } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("id, name, role, image_url, image_position")
        .order("sort_order");
      if (error) throw error;
      return data as DbTeamMember[];
    },
  });

  return (
    <Layout transparentNav>
      <SEOHead
        title="About Us — Registered Architects in Kumbakonam & Delta District"
        description="Meet the COA-registered team behind Dhivisha Architects. 350+ projects, 15+ awards, 10+ years of innovative architectural design in Kumbakonam, serving the entire Cauvery Delta region."
        path="/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      {leaders.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "@id": "https://dhivishaarchitects.com/#organization",
              name: "Dhivisha Architects and Interiors",
              member: leaders.map((t) => ({
                "@type": "Person",
                name: t.name,
                jobTitle: t.role,
                url: `https://dhivishaarchitects.com/leaders/${t.slug}`,
                worksFor: { "@id": "https://dhivishaarchitects.com/#organization" },
              })),
            }),
          }}
        />
      )}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=70" alt="Architecture team" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">About Us</h1>
          <p className="text-lg text-primary-foreground/80">Learn about our notable clients and team members</p>
        </motion.div>
      </section>

      {/* Stats with Logo */}
      <section className="py-16 bg-muted">
        <div className="max-w-5xl mx-auto px-4">
          <Suspense fallback={<div className="h-24 mx-auto mb-8" />}>
            <Logo3D size="lg" animate glow className="mx-auto mb-8" />
          </Suspense>
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat) => (
              <motion.div key={stat.label} variants={staggerItem} className="text-center">
                <p className="text-4xl font-bold text-primary">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl font-bold text-foreground mb-6">Who We Are</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Here you can find we are proud of our notable clients and information about our team members. Dhivisha Architects has been at the forefront of innovative architectural design for over 10 years.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our firm brings together a diverse team of architects, designers, and planners who share a passion for creating spaces that are both functional and inspiring.
            </p>
            <div className="flex gap-6 mt-6">
              <div className="flex items-center gap-2 text-sm text-foreground"><Target size={18} className="text-primary" /> Vision-Driven</div>
              <div className="flex items-center gap-2 text-sm text-foreground"><Eye size={18} className="text-primary" /> Detail-Oriented</div>
              <div className="flex items-center gap-2 text-sm text-foreground"><Users size={18} className="text-primary" /> Client-First</div>
            </div>
          </motion.div>
          <motion.div
            className="rounded overflow-hidden"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80" alt="Our office" className="w-full h-[400px] object-cover" loading="lazy" decoding="async" />
          </motion.div>
        </div>
      </section>

      {/* Valued Clients */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Valued Clients</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
              We are proud to have worked with distinguished leaders and organizations who trust us with their vision.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {clients.map((client) => (
              <motion.div
                key={client.id}
                variants={staggerItem}
                whileHover={{ y: -4, scale: 1.02 }}
                className="bg-card rounded-lg p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center border border-border/50"
              >
                <div className="w-20 h-20 rounded-full overflow-hidden mb-4 border-2 border-primary/20 bg-white flex items-center justify-center p-1">
                  {client.image_url && (
                    <img
                      src={client.image_url}
                      alt={client.name}
                      className="w-full h-full object-contain rounded-full"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                </div>
                <h3 className="font-semibold text-foreground text-sm leading-tight mb-1">{client.name}</h3>
                <p className="text-xs text-primary font-medium">{client.subtitle}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Our Leadership</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
              Visionaries who shape our practice. Click any card to learn more.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {leaders.map((leader) => (
              <motion.div
                key={leader.id}
                variants={staggerItem}
                whileHover={{ y: -6 }}
              >
                <Link
                  to={`/leaders/${leader.slug}`}
                  className="block bg-card rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group text-left border border-border/50 focus:outline-none focus:ring-2 focus:ring-primary"
                  aria-label={`View profile of ${leader.name}`}
                >
                  <div className="h-72 overflow-hidden relative">
                    {leader.image_url && (
                      <img
                        src={leader.image_url}
                        alt={leader.name}
                        style={{ objectPosition: leader.image_position }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-foreground text-lg">{leader.name}</h3>
                    <p className="text-primary text-sm font-medium mt-1">{leader.role}</p>
                    {leader.tagline && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{leader.tagline}</p>
                    )}
                    <p className="text-xs text-primary mt-3 group-hover:underline">
                      View profile →
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-muted content-auto">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-4">Our Team</h2>
            <p className="text-center text-muted-foreground max-w-2xl mx-auto mb-12 text-lg leading-relaxed">
              Behind every iconic structure stands a team of passionate minds and dedicated professionals. Our architects, engineers, and designers don't just build spaces — they craft legacies that stand the test of time.
            </p>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {team.map((member) => (
              <motion.div
                key={member.id}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                className="bg-card rounded overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group relative"
              >
                <div className="h-40 overflow-hidden relative">
                  {member.image_url && (
                    <img
                      src={member.image_url}
                      alt={member.name}
                      style={{ objectPosition: member.image_position }}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className="absolute inset-0 bg-secondary/80 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="text-center px-6">
                      <h3 className="font-bold text-primary-foreground text-sm mb-1">{member.name}</h3>
                      <div className="w-8 h-0.5 bg-primary mx-auto mb-2" />
                      <p className="text-primary text-xs font-semibold tracking-wide uppercase">{member.role}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-foreground text-sm">{member.name}</h3>
                  <p className="text-primary text-xs font-medium">{member.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

    </Layout>
  );
};

export default About;
